import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, DollarSign, Activity, ArrowUpRight, 
  RefreshCw, BarChart3
} from "lucide-react";
import { Link } from "react-router-dom";
import { SkeletonDashboardWidget } from "@/components/ui/skeleton";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  const token = localStorage.getItem("user_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatCurrency = (amount) => {
  if (amount >= 1000000) {
    return `Rp ${(amount / 1000000).toFixed(1)}Jt`;
  } else if (amount >= 1000) {
    return `Rp ${(amount / 1000).toFixed(0)}K`;
  }
  return `Rp ${(amount || 0).toLocaleString('id-ID')}`;
};

export default function DashboardSummaryWidget() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/user/daily-stats`, {
        headers: getAuthHeader()
      });
      
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch daily stats:", err);
      setError("Gagal memuat statistik");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (isLoading) {
    return <SkeletonDashboardWidget />;
  }

  if (error || !stats) {
    return null; // Hide widget if error
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass-card rounded-2xl p-4 mb-6 dark:bg-slate-800/50 dark:border-slate-700"
      data-testid="dashboard-summary-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-white text-sm">Ringkasan Hari Ini</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={fetchStats}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <Link 
            to="/my-reports"
            className="text-xs text-sky-600 hover:text-sky-700 dark:text-sky-400 flex items-center gap-1"
          >
            Detail
            <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Transaksi Hari Ini */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 border border-cyan-100 dark:border-cyan-800/30">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
            <span className="text-xs text-slate-500 dark:text-slate-400">Transaksi</span>
          </div>
          <p className="text-xl font-bold text-slate-800 dark:text-white" data-testid="today-transactions">
            {stats.today_transactions || 0}
          </p>
          {stats.transaction_change !== undefined && stats.transaction_change !== 0 && (
            <p className={`text-xs flex items-center gap-1 ${
              stats.transaction_change >= 0 ? 'text-emerald-600' : 'text-red-500'
            }`}>
              <TrendingUp className={`w-3 h-3 ${stats.transaction_change < 0 ? 'rotate-180' : ''}`} />
              {stats.transaction_change > 0 ? '+' : ''}{stats.transaction_change}% dari kemarin
            </p>
          )}
        </div>

        {/* Revenue Hari Ini */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border border-emerald-100 dark:border-emerald-800/30">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs text-slate-500 dark:text-slate-400">Pendapatan</span>
          </div>
          <p className="text-lg font-bold text-slate-800 dark:text-white" data-testid="today-revenue">
            {formatCurrency(stats.today_revenue || 0)}
          </p>
          {stats.revenue_change !== undefined && stats.revenue_change !== 0 && (
            <p className={`text-xs flex items-center gap-1 ${
              stats.revenue_change >= 0 ? 'text-emerald-600' : 'text-red-500'
            }`}>
              <TrendingUp className={`w-3 h-3 ${stats.revenue_change < 0 ? 'rotate-180' : ''}`} />
              {stats.revenue_change > 0 ? '+' : ''}{stats.revenue_change}% dari kemarin
            </p>
          )}
        </div>

        {/* Admin Fee */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-100 dark:border-amber-800/30">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-xs text-slate-500 dark:text-slate-400">Admin Fee</span>
          </div>
          <p className="text-lg font-bold text-slate-800 dark:text-white" data-testid="today-admin-fee">
            {formatCurrency(stats.today_admin_fee || 0)}
          </p>
        </div>

        {/* Saldo */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-100 dark:border-purple-800/30">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-xs text-slate-500 dark:text-slate-400">Saldo</span>
          </div>
          <p className="text-lg font-bold text-slate-800 dark:text-white" data-testid="current-balance">
            {formatCurrency(stats.balance || 0)}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
